// Template for Global Commitment Sppi Individual Profile page

import React from 'react';
import styled from '@emotion/styled/macro';
import siteVars from '../variables';
import vars from '@variables';
import Layout from '@layouts/layout';
import SectionLayout from '@layouts/section-layout';
import ProfileContainer from '@components/global-commitment/gc-profile-container/gc-profile-container';
import HorizontalTable from '@components/horizontal-table/horizontal-table';
import TableRow from '@components/horizontal-table/table-row';
import SecondaryNav from '@components/secondary-nav/secondary-nav';
import createGcMetaData from '../utils/global-commitment/gc-metadata';
import SubHeader from '@components/global-commitment/sub-header/sub-header';
import FormattedText from '@components/global-commitment/formatted-text/formatted-text';
import {
  generateGcDropdownLinks,
  normalizeUrl,
} from '../utils/global-commitment/helper-functions';
import { graphql } from 'gatsby';
import { getFooterData } from '../utils/translation-functions';
import { PAtom, SubHeaderAtom } from '@atoms/text-atoms/text-atoms';
import { convertStringToBoolean } from '@utils/utils';
import { transformTags } from '../utils/transform-data-functions.js';
import { FlexibleLinkAtom } from '@atoms/link-atoms/link-atoms';

export const query = graphql`
  query GcSppiPageQuery($id: String) {
    categoryLinks: allGcSppiDataCsv {
      nodes {
        organisation_name__b_1
        slug__b_4
      }
    }
    gcSppiPage: gcSppiDataCsv(id: { eq: $id }) {
      id
      category__b_0
      organisation_name__b_1
      slug__b_4
      meta_title__b_6_1
      meta_description__b_6_2
      previous_report_url__b_5
      _12_months_time_frame_for_quantitative_data__2_1
      activity_and_progress_made_over_the_reporting_period__20_1_2_1_1
      activity_and_progress_made_over_the_reporting_period__20_1_2_2_1
      activity_and_progress_made_over_the_reporting_period__20_1_2_3_1
      activity_and_progress_made_over_the_reporting_period__20_1_2_4_1
      activity_and_progress_made_over_the_reporting_period__20_1_2_5_1
      activity_and_progress_made_over_the_reporting_period__20_1_2_6_1
      additional_details_on_investments__21_3
      commitment_1_block__display__c1_display
      commitment_2_block__display__c2_display
      commitment_3_block__display__c3_display
      commitment_4_block__display__c4_display
      commitment_5_block__display__c5_display
      company_details_block__display__company_details_display
      data_verification_assurance_status__2_2
      description__1_1
      description__20_1_1_1_1
      description__20_1_1_2_1
      description__20_1_1_3_1
      description__20_1_1_4_1
      description__20_1_1_5_1
      description__20_1_1_6_1
      ellen_macarthur_foundations_plastics_pact_network_membership__as_of_18_05_2020___2_7
      endnotes_block__display__endnotes_display
      headquarters_location__1_5
      investments_block__display__investments_display
      joined_the_global_commitment__join_date
      organisation_name_sortable__b_1_bis
      other_commitment_block__display__c_other_display
      planned_actions_by_2025__20_1_3_1_1
      planned_actions_by_2025__20_1_3_2_1
      planned_actions_by_2025__20_1_3_3_1
      planned_actions_by_2025__20_1_3_4_1
      planned_actions_by_2025__20_1_3_5_1
      planned_actions_by_2025__20_1_3_6_1
      publish_report__b_3
      regions_active__1_6
      reporting_details_block__display__reporting_details_display
      revenue_band__usd___1_4_2_1
      thirdparty_verification_assurance__2_3
      total_invested_committed_over_the_reporting_period__21_1_1
      total_invested_committed_over_the_reporting_period__alternative_text__21_2
      webpage_or_report_addressing_progress__1_3
      website__1_2
    }
  }
`;

const Page = ({ data, className, pageContext }) => {
  // Primary nav and footer data handling.
  const footerData = getFooterData('en-GB');

  const pageData = data.gcSppiPage;
  const linkData = data.categoryLinks.nodes;

  const supplierSecondaryNavData = [
    {
      isVisible: convertStringToBoolean(
        pageData.company_details_block__display__company_details_display
      ),
      title: 'company details',
      url: '#company-details',
    },
    {
      isVisible: convertStringToBoolean(
        pageData.reporting_details_block__display__reporting_details_display
      ),
      title: 'reporting details',
      url: '#reporting-details',
    },
    {
      isVisible: true,
      title: 'commitments',
      url: '#commitments',
    },
    {
      isVisible: convertStringToBoolean(
        pageData.investments_block__display__investments_display
      ),
      title: 'investments',
      url: '#investments',
    },
    {
      isVisible: convertStringToBoolean(
        pageData.endnotes_block__display__endnotes_display
      ),
      title: 'end notes',
      url: '#end-notes',
    },
  ].filter(element => element.isVisible === true);

  const organisationListingUrl = `${siteVars.GLOBAL_COMMITMENT_BASE_PATH}/${siteVars.GLOBAL_COMMITMENT_LISTING_PAGE_SLUG}`;

  const title = pageData.organisation_name__b_1;
  const category = pageData.category__b_0;
  const metaTitle = pageData.meta_title__b_6_1;
  const metaDescription = pageData.meta_description__b_6_2;
  const website = pageData.website__1_2
    ? normalizeUrl(pageData.website__1_2)
    : null;
  const webreport = pageData.webpage_or_report_addressing_progress__1_3
    ? normalizeUrl(pageData.webpage_or_report_addressing_progress__1_3)
    : null;

  // generate proper links for companies dropdown
  const gcDropdownLinks = generateGcDropdownLinks(linkData, category, title);

  // Tags data handling
  const transformedTags = transformTags(['Plastics']);

  const metaData = createGcMetaData(title, metaTitle, metaDescription);

  const gtmDataLayerVars = {
    pageId: pageContext.id,
    event: 'Page View',
    pageType: `standard page${
      pageContext.pageType ? ` - ${pageContext.pageType}` : null
    }`,
    topics: transformedTags,
  };

  return (
    <Layout
      footerData={footerData}
      translate={false}
      className={className}
      metaData={metaData}
      gtmDataLayerVars={gtmDataLayerVars}
      globals={pageContext?.globals ? pageContext.globals : null}
    >
      {' '}
      <div className="wrapper">
        <SectionLayout>
          <ProfileContainer>
            <SubHeader
              title={title}
              category={category}
              firstYearReport={pageData.first_year__rep}
              previousYearSlug={pageData.previous_report_url__b_5}
              organisationListingUrl={organisationListingUrl}
              linkData={gcDropdownLinks}
            />

            <SecondaryNav tabs={supplierSecondaryNavData} />
            <div id="company-details" className="link-destination" />
            <HorizontalTable
              title="Company Details"
              isVisible={convertStringToBoolean(
                pageData.company_details_block__display__company_details_display
              )}
            >
              {pageData.description__1_1 ? (
                <TableRow label="Description">
                  <FormattedText inputText={pageData.description__1_1} />
                </TableRow>
              ) : null}
              {website ? (
                <TableRow label="Website">
                  <PAtom>
                    {website && website !== 'N/A' ? (
                      <FlexibleLinkAtom url={website} />
                    ) : (
                      <span>N/A</span>
                    )}
                  </PAtom>
                </TableRow>
              ) : null}
              {pageData.revenue_band__usd___1_4_2_1 ? (
                <TableRow label="Revenue band (USD)">
                  <PAtom>{pageData.revenue_band__usd___1_4_2_1}</PAtom>
                </TableRow>
              ) : null}
              {pageData.headquarters_location__1_5 ? (
                <TableRow label="Headquarters location">
                  <PAtom>{pageData.headquarters_location__1_5}</PAtom>
                </TableRow>
              ) : null}
              {pageData.regions_active__1_6 ? (
                <TableRow label="Regions active">
                  <FormattedText inputText={pageData.regions_active__1_6} />
                </TableRow>
              ) : null}
            </HorizontalTable>

            <div id="reporting-details" className="link-destination" />
            <HorizontalTable
              title="Reporting details"
              isVisible={convertStringToBoolean(
                pageData.reporting_details_block__display__reporting_details_display
              )}
            >
              {pageData.data_verification_assurance_status__2_2 ? (
                <TableRow label="Data verification/assurance status">
                  <PAtom>
                    {pageData.data_verification_assurance_status__2_2}
                  </PAtom>
                </TableRow>
              ) : null}
              {pageData._12_months_time_frame_for_quantitative_data__2_1 ? (
                <TableRow label="12-month quantitative data time frame">
                  <PAtom>
                    {pageData._12_months_time_frame_for_quantitative_data__2_1}
                  </PAtom>
                </TableRow>
              ) : null}
              {pageData.joined_the_global_commitment__join_date ? (
                <TableRow label="Joined the Global Commitment">
                  <PAtom>
                    {pageData.joined_the_global_commitment__join_date}
                  </PAtom>
                </TableRow>
              ) : null}
              {pageData.ellen_macarthur_foundations_plastics_pact_network_membership__as_of_18_05_2020___2_7 ? (
                <TableRow label="Ellen MacArthur Foundation's Plastics Pact Network membership (as of 18/05/2021)">
                  <PAtom>
                    {
                      pageData.ellen_macarthur_foundations_plastics_pact_network_membership__as_of_18_05_2020___2_7
                    }
                  </PAtom>
                </TableRow>
              ) : null}
              {webreport ? (
                <TableRow label="Webpage or report addressing progress">
                  <PAtom>
                    {webreport && webreport !== 'N/A' ? (
                      <FlexibleLinkAtom url={webreport} />
                    ) : (
                      <span>N/A</span>
                    )}
                  </PAtom>
                </TableRow>
              ) : null}
            </HorizontalTable>

            <div id="commitments" className="link-destination" />
            <SubHeaderAtom displaySeparator={true}>Commitments</SubHeaderAtom>
            <HorizontalTable
              title="Commitment 1:"
              isVisible={convertStringToBoolean(
                pageData.commitment_1_block__display__c1_display
              )}
            >
              {pageData.description__20_1_1_1_1 ? (
                <TableRow label="Description">
                  <FormattedText inputText={pageData.description__20_1_1_1_1} />
                </TableRow>
              ) : null}
              {pageData.activity_and_progress_made_over_the_reporting_period__20_1_2_1_1 ? (
                <TableRow label="Activity and progress made over the reporting period">
                  <FormattedText
                    inputText={
                      pageData.activity_and_progress_made_over_the_reporting_period__20_1_2_1_1
                    }
                  />
                </TableRow>
              ) : null}
              {pageData.planned_actions_by_2025__20_1_3_1_1 ? (
                <TableRow label="Planned actions by 2025">
                  <FormattedText
                    inputText={pageData.planned_actions_by_2025__20_1_3_1_1}
                  />
                </TableRow>
              ) : null}
            </HorizontalTable>
            <HorizontalTable
              title="Commitment 2:"
              isVisible={convertStringToBoolean(
                pageData.commitment_2_block__display__c2_display
              )}
            >
              {pageData.description__20_1_1_2_1 ? (
                <TableRow label="Description">
                  <FormattedText inputText={pageData.description__20_1_1_2_1} />
                </TableRow>
              ) : null}
              {pageData.activity_and_progress_made_over_the_reporting_period__20_1_2_2_1 ? (
                <TableRow label="Activity and progress made over the reporting period">
                  <FormattedText
                    inputText={
                      pageData.activity_and_progress_made_over_the_reporting_period__20_1_2_2_1
                    }
                  />
                </TableRow>
              ) : null}
              {pageData.planned_actions_by_2025__20_1_3_2_1 ? (
                <TableRow label="Planned actions by 2025">
                  <FormattedText
                    inputText={pageData.planned_actions_by_2025__20_1_3_2_1}
                  />
                </TableRow>
              ) : null}
            </HorizontalTable>
            <HorizontalTable
              title="Commitment 3:"
              isVisible={convertStringToBoolean(
                pageData.commitment_3_block__display__c3_display
              )}
            >
              {pageData.description__20_1_1_3_1 ? (
                <TableRow label="Description">
                  <FormattedText inputText={pageData.description__20_1_1_3_1} />
                </TableRow>
              ) : null}
              {pageData.activity_and_progress_made_over_the_reporting_period__20_1_2_3_1 ? (
                <TableRow label="Activity and progress made over the reporting period">
                  <FormattedText
                    inputText={
                      pageData.activity_and_progress_made_over_the_reporting_period__20_1_2_3_1
                    }
                  />
                </TableRow>
              ) : null}
              {pageData.planned_actions_by_2025__20_1_3_3_1 ? (
                <TableRow label="Planned actions by 2025">
                  <FormattedText
                    inputText={pageData.planned_actions_by_2025__20_1_3_3_1}
                  />
                </TableRow>
              ) : null}
            </HorizontalTable>
            <HorizontalTable
              title="Commitment 4:"
              isVisible={convertStringToBoolean(
                pageData.commitment_4_block__display__c4_display
              )}
            >
              {pageData.description__20_1_1_4_1 ? (
                <TableRow label="Description">
                  <FormattedText inputText={pageData.description__20_1_1_4_1} />
                </TableRow>
              ) : null}
              {pageData.activity_and_progress_made_over_the_reporting_period__20_1_2_4_1 ? (
                <TableRow label="Activity and progress made over the reporting period">
                  <FormattedText
                    inputText={
                      pageData.activity_and_progress_made_over_the_reporting_period__20_1_2_4_1
                    }
                  />
                </TableRow>
              ) : null}
              {pageData.planned_actions_by_2025__20_1_3_4_1 ? (
                <TableRow label="Planned actions by 2025">
                  <FormattedText
                    inputText={pageData.planned_actions_by_2025__20_1_3_4_1}
                  />
                </TableRow>
              ) : null}
            </HorizontalTable>
            <HorizontalTable
              title="Commitment 5:"
              isVisible={convertStringToBoolean(
                pageData.commitment_5_block__display__c5_display
              )}
            >
              {pageData.description__20_1_1_5_1 ? (
                <TableRow label="Description">
                  <FormattedText inputText={pageData.description__20_1_1_5_1} />
                </TableRow>
              ) : null}
              {pageData.activity_and_progress_made_over_the_reporting_period__20_1_2_5_1 ? (
                <TableRow label="Activity and progress made over the reporting period">
                  <FormattedText
                    inputText={
                      pageData.activity_and_progress_made_over_the_reporting_period__20_1_2_5_1
                    }
                  />
                </TableRow>
              ) : null}
              {pageData.planned_actions_by_2025__20_1_3_5_1 ? (
                <TableRow label="Planned actions by 2025">
                  <FormattedText
                    inputText={pageData.planned_actions_by_2025__20_1_3_5_1}
                  />
                </TableRow>
              ) : null}
            </HorizontalTable>
            <HorizontalTable
              title="Additional commitment(s)"
              isVisible={convertStringToBoolean(
                pageData.other_commitment_block__display__c_other_display
              )}
            >
              {pageData.description__20_1_1_6_1 ? (
                <TableRow label="Description">
                  <FormattedText inputText={pageData.description__20_1_1_6_1} />
                </TableRow>
              ) : null}
              {pageData.activity_and_progress_made_over_the_reporting_period__20_1_2_6_1 ? (
                <TableRow label="Activity and progress made over the reporting period">
                  <FormattedText
                    inputText={
                      pageData.activity_and_progress_made_over_the_reporting_period__20_1_2_6_1
                    }
                  />
                </TableRow>
              ) : null}
              {pageData.planned_actions_by_2025__20_1_3_6_1 ? (
                <TableRow label="Planned actions by 2025">
                  <FormattedText
                    inputText={pageData.planned_actions_by_2025__20_1_3_6_1}
                  />
                </TableRow>
              ) : null}
            </HorizontalTable>
            <div id="investments" className="link-destination" />
            <HorizontalTable
              title="Investments"
              isVisible={convertStringToBoolean(
                pageData.investments_block__display__investments_display
              )}
            >
              <TableRow label="Total invested/committed over the reporting period towards achieving the commitments">
                {pageData.total_invested_committed_over_the_reporting_period__21_1_1 ? (
                  <FormattedText
                    inputText={
                      pageData.total_invested_committed_over_the_reporting_period__21_1_1
                    }
                  />
                ) : (
                  <PAtom>
                    {
                      pageData.total_invested_committed_over_the_reporting_period__alternative_text__21_2
                    }
                  </PAtom>
                )}
              </TableRow>

              {pageData.additional_details_on_investments__21_3 ? (
                <TableRow label="Additional details on investments">
                  <FormattedText
                    inputText={pageData.additional_details_on_investments__21_3}
                  />
                </TableRow>
              ) : null}
            </HorizontalTable>

            <div id="end-notes" className="link-destination" />
            <HorizontalTable
              title="End Notes"
              isVisible={convertStringToBoolean(
                pageData.endnotes_block__display__endnotes_display
              )}
            >
              {pageData.thirdparty_verification_assurance__2_3 ? (
                <TableRow label="Third-party verification/assurance">
                  <FormattedText
                    inputText={pageData.thirdparty_verification_assurance__2_3}
                  />
                </TableRow>
              ) : null}
            </HorizontalTable>
          </ProfileContainer>
        </SectionLayout>
      </div>
    </Layout>
  );
};

const StyledGcSppiPage = styled(Page)`
  .wrapper {
    margin: 12rem ${vars.spacingL};
  }
`;

export default StyledGcSppiPage;
